import { Link } from 'react-router-dom';
import { ArrowRight, Brain, LineChart, ListChecks, MessageSquare, Shield, Target, Users, Zap } from 'lucide-react';
import { AboutStructuredData } from '@/components/seo/AboutStructuredData';
import { PageStructuredData } from '@/components/seo/PageStructuredData';
import { SpotlightCard } from '@/components/home/primitives';
import { CtaPanel, PageHero, PageShell, Reveal, Section, SectionHeading } from '@/components/page/PageKit';
import { useSEO } from '@/hooks/useSEO';

const principles = [
  { icon: LineChart, title: 'Evidence over intuition', description: 'Hiring decisions backed by structured data, not hunches.' },
  { icon: ListChecks, title: 'Structured process over gut feeling', description: 'Consistent evaluation criteria for every candidate.' },
  { icon: Brain, title: 'AI as decision support', description: 'Technology that informs, never replaces, human judgment.' },
  { icon: Users, title: 'Human judgment remains central', description: 'Final hiring decisions always belong to your team.' },
  { icon: Shield, title: 'Privacy and security by design', description: 'Candidate data protected at every step.', link: '/security' },
];
const founders = [
  { initials: 'AB', name: 'Amine BOUKIOUD', role: 'Co-Founder' },
  { initials: 'MB', name: 'Mustapha BOUKIOUD', role: 'Co-Founder' },
];
const customerPoints = [
  { icon: MessageSquare, text: 'Direct access to founders — not support tickets that disappear into queues' },
  { icon: Zap, text: 'Fast feedback loops — ideas and issues are addressed in days, not quarters' },
  { icon: Target, text: 'Long-term product focus — we build for sustainable value, not short-term metrics' },
];

export default function About() {
  useSEO({ title: 'About Beatview | Founder-Led AI Recruiting Agent', description: 'Beatview is a founder-led company building the AI recruiting agent for recruitment agencies, guided by evidence over intuition and human-first AI.', canonicalUrl: 'https://beatview.ai/about' });

  return (
    <PageShell>
      <AboutStructuredData />
      <PageStructuredData name="About Us" description="Learn about Beatview's mission and team." url="https://beatview.ai/about" type="AboutPage" breadcrumbs={[{ name: 'About' }]} />

      <PageHero eyebrow="About Beatview" title={<>Why we <span className="bv-hero-highlight">built Beatview</span></>}>
        <div className="max-w-3xl space-y-6 text-lg leading-8 text-bv-ink2">
          <p>Modern hiring is broken. Teams receive hundreds of applications but lack the time to evaluate each one properly. Interviews vary by interviewer, making fair comparisons impossible. Great candidates get overlooked while less qualified ones advance based on gut feelings.</p>
          <p>Existing tools didn't solve the core problem—they automated scheduling or added more forms. What was missing was a way to evaluate candidates consistently, fairly, and with evidence that teams could actually trust.</p>
          <p className="font-display text-2xl font-medium leading-9 text-bv-ink">Beatview is our answer: a structured hiring process that gives every candidate a fair evaluation, while giving teams the clarity to make confident decisions.</p>
          <Link to="/features" className="group inline-flex items-center gap-1.5 text-base font-semibold text-bv-blue">See how our platform works <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
        </div>
      </PageHero>

      <Section tone="wash" labelledBy="principles-title">
        <SectionHeading id="principles-title" eyebrow="What we believe" title="Our principles" />
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {principles.map(({ icon: Icon, title, description, link }, i) => {
            const card = (
              <SpotlightCard className="flex h-full flex-col p-6">
                <span className="font-display text-sm font-semibold text-bv-mute">0{i + 1}</span>
                <span className="mt-5 flex h-11 w-11 items-center justify-center rounded-xl bg-bv-blue-wash text-bv-blue transition-colors group-hover:bg-bv-ink group-hover:text-white"><Icon className="h-5 w-5" /></span>
                <h3 className="mt-5 font-display text-lg font-semibold leading-snug text-bv-ink">{title}</h3>
                <p className="mt-2 text-[15px] leading-6 text-bv-ink2">{description}</p>
              </SpotlightCard>
            );
            return <Reveal key={title} delay={i * 0.06}>{link ? <Link to={link} className="block h-full">{card}</Link> : card}</Reveal>;
          })}
        </div>
      </Section>

      <Section tone="dark" labelledBy="founders-title">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionHeading tone="dark" id="founders-title" eyebrow="The team" title="Founder-led by design" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg leading-8 text-white/70">Beatview is built and led by its founders, who remain closely involved in product development, security decisions, and customer experience. This isn't a company managed by committees or distant investors—it's founder-led by design.</p>
              <p className="mt-4 text-lg font-medium text-white">This means clear ownership, faster decisions, and direct accountability.</p>
            </Reveal>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {founders.map((founder, i) => (
              <Reveal key={founder.name} delay={0.1 + i * 0.1}>
                <div className="rounded-3xl border border-white/10 bg-white/[0.05] p-8 text-center">
                  <span className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-[conic-gradient(from_0deg,hsl(var(--hero-highlight-start)),hsl(var(--blue)),hsl(var(--hero-highlight-end)),hsl(var(--hero-highlight-start)))] p-[3px]">
                    <span className="flex h-full w-full items-center justify-center rounded-full bg-bv-ink font-display text-3xl font-semibold">{founder.initials}</span>
                  </span>
                  <h3 className="mt-6 font-display text-xl font-semibold">{founder.name}</h3>
                  <p className="mt-1 text-white/60">{founder.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section tone="white" labelledBy="customers-title" narrow>
        <SectionHeading id="customers-title" align="center" eyebrow="How we work" title="Built with customers, not committees" description="We believe the best products are built through direct conversations with the people who use them." />
        <div className="mt-12 space-y-4">
          {customerPoints.map(({ icon: Icon, text }, i) => (
            <Reveal key={text} delay={i * 0.08}>
              <div className="flex items-center gap-5 rounded-2xl border border-bv-blue-line bg-bv-blue-wash/40 p-5 sm:p-6">
                <span className="bv-btn-gradient flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white"><Icon className="h-5 w-5" /></span>
                <p className="text-[17px] leading-7 text-bv-ink">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal><p className="mt-12 border-t border-bv-blue-line pt-8 text-center text-bv-ink2">Beatview was built with three UK recruitment agencies during beta.</p></Reveal>
      </Section>

      <CtaPanel eyebrow="Talk to the founders" title={<>Have questions? <span className="bv-hero-highlight">Get in touch.</span></>} description="Tell us how your agency screens and interviews today — you’ll speak directly with the people building Beatview." primaryLabel="Contact us" primaryHref="/contact" secondary={{ label: 'Start for free', href: 'https://platform.beatview.ai/' }} />
    </PageShell>
  );
}
